import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

// Fallback geometry constants (used before measurement / in jsdom, where
// getBoundingClientRect reports zeros). Real row pitch is taller than the node
// because each row stacks a label + hint, so a guessed pitch drifts off the
// nodes — hence the hook measures the actual node centers in a real layout.
const RAIL_PAD_TOP = 22; // .nb-cdrawer-rail padding-top
const NODE_SIZE = 28; // .nb-cdrawer-node height
const NODE_GAP = 26; // vertical gap between step rows
const NODE_PITCH = NODE_SIZE + NODE_GAP; // fallback center-to-center spacing
const FIRST_NODE_CENTER = RAIL_PAD_TOP + NODE_SIZE / 2;

/**
 * Measures the rail's connecting-line geometry from the real DOM node centers so
 * the line spans exactly node-1-center → node-N-center (and the completed-fraction
 * fill lands on the active node), independent of row height / font metrics. Re-measures
 * on step-count change and on rail resize. Returns a constant estimate until a real
 * layout is measured, so first paint and jsdom tests stay deterministic.
 */
export function useRailLineGeometry(stepCount: number): {
  navRef: RefObject<HTMLElement>;
  lineGeom: CSSProperties;
} {
  const segments = Math.max(stepCount - 1, 0);
  const navRef = useRef<HTMLElement>(null);
  const [measured, setMeasured] = useState<{ top: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const measure = () => {
      const nodes = nav.querySelectorAll('.nb-cdrawer-node');
      if (nodes.length < 2) return setMeasured(null);
      const navTop = nav.getBoundingClientRect().top;
      const first = nodes[0].getBoundingClientRect();
      const last = nodes[nodes.length - 1].getBoundingClientRect();
      const firstCenter = first.top - navTop + first.height / 2;
      const lastCenter = last.top - navTop + last.height / 2;
      // Trust a real layout only (jsdom reports zeros → keep the fallback).
      setMeasured(
        lastCenter > firstCenter ? { top: firstCenter, height: lastCenter - firstCenter } : null,
      );
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(nav);
    return () => ro?.disconnect();
  }, [stepCount]);

  const lineGeom: CSSProperties = measured ?? {
    top: FIRST_NODE_CENTER,
    height: segments * NODE_PITCH,
  };
  return { navRef, lineGeom };
}
